import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { db } from '../../services/firebase';
import {
  doc,
  setDoc,
  getDoc,
  updateDoc,
  collection,
  getDocs,
} from 'firebase/firestore';
import {
  Shield,
  Building,
  Search,
  Settings,
  Save,
  X,
  Check,
  Users,
  Eye,
  Edit,
  Trash2,
  FileText,
  DollarSign,
  Calendar,
  BarChart3,
  Plus,
  UserPlus,
  AlertCircle,
} from 'lucide-react';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import {
  JobTitle,
  Department,
  JOB_TITLE_NAMES,
  getEmployeePermissions,
  EmployeePermissions,
  getJobTitleDisplayName,
} from '../../types/newRoleSystem';
import toast from 'react-hot-toast';
import DepartmentManagement from '../../components/hr/DepartmentManagement';

const RolesManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'permissions' | 'departments'>(
    'permissions'
  );
  const [jobTitles, setJobTitles] = useState<
    Array<{
      title: JobTitle;
      permissions: EmployeePermissions;
    }>
  >([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedJobTitle, setSelectedJobTitle] = useState<JobTitle | null>(
    null
  );
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [editingPermissions, setEditingPermissions] =
    useState<EmployeePermissions | null>(null);
  const [saving, setSaving] = useState(false);
  const [showAddJobTitleModal, setShowAddJobTitleModal] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [jobTitleToDelete, setJobTitleToDelete] = useState<JobTitle | null>(
    null
  );
  const [newJobTitleName, setNewJobTitleName] = useState('');

  const { user } = useAuth();

  const loadData = async () => {
    try {
      setLoading(true);

      // تحميل صلاحيات المسميات الوظيفية من Firebase
      const permissionsDoc = await getDoc(
        doc(db, 'system_settings', 'job_title_permissions')
      );
      if (permissionsDoc.exists()) {
        const data = permissionsDoc.data();
        if (data.jobTitles) {
          setJobTitles(data.jobTitles);
        } else {
          // إنشاء البيانات الافتراضية إذا لم تكن موجودة
          await initializeDefaultData();
        }
      } else {
        // إنشاء البيانات الافتراضية إذا لم تكن موجودة
        await initializeDefaultData();
      }
    } catch (error: any) {
      console.error('Error loading job titles and permissions:', error);

      // في حالة خطأ الصلاحيات، استخدم البيانات المحلية الافتراضية
      if (error?.code === 'permission-denied') {
        toast('تم استخدام البيانات المحلية بسبب قيود الصلاحيات', {
          icon: '⚠️',
          duration: 4000,
        });
        initializeLocalData();
      } else {
        toast.error('فشل في تحميل البيانات');
        initializeLocalData(); // fallback للبيانات المحلية
      }
    } finally {
      setLoading(false);
    }
  };

  const initializeLocalData = () => {
    // إنشاء بيانات محلية افتراضية بدون Firebase
    const jobTitlesList: JobTitle[] = [
      'Admin',
      'Supervisor',
      'Senior',
      'Junior',
    ];
    const defaultJobTitles = jobTitlesList.map(title => ({
      title,
      permissions: getEmployeePermissions('IT' as Department, title),
    }));

    setJobTitles(defaultJobTitles);
  };

  const initializeDefaultData = async () => {
    // إنشاء بيانات المسميات الوظيفية مع الصلاحيات الافتراضية
    const jobTitlesList: JobTitle[] = [
      'Admin',
      'Supervisor',
      'Senior',
      'Junior',
    ];
    const defaultJobTitles = jobTitlesList.map(title => ({
      title,
      permissions: getEmployeePermissions('IT' as Department, title),
    }));

    setJobTitles(defaultJobTitles);

    // حفظ البيانات الافتراضية في Firebase
    await setDoc(doc(db, 'system_settings', 'job_title_permissions'), {
      jobTitles: defaultJobTitles,
      updatedAt: new Date(),
      updatedBy: user?.id || 'system',
    });
  };

  useEffect(() => {
    loadData();
  }, []);

  const filteredJobTitles = jobTitles.filter(jobTitle => {
    const displayName = getJobTitleDisplayName(jobTitle.title);
    return displayName.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const handleEditPermissions = (jobTitle: JobTitle) => {
    const jobTitleData = jobTitles.find(jt => jt.title === jobTitle);
    if (jobTitleData) {
      setSelectedJobTitle(jobTitle);
      setEditingPermissions({ ...jobTitleData.permissions });
      setShowPermissionsModal(true);
    }
  };

  const handleSavePermissions = async () => {
    if (!selectedJobTitle || !editingPermissions) return;

    try {
      setSaving(true);

      // تحديث الصلاحيات للمسمى الوظيفي محلياً
      const updatedJobTitles = jobTitles.map(jt =>
        jt.title === selectedJobTitle
          ? { ...jt, permissions: editingPermissions }
          : jt
      );

      setJobTitles(updatedJobTitles);

      // محاولة حفظ التحديثات في Firebase
      try {
        await updateDoc(doc(db, 'system_settings', 'job_title_permissions'), {
          jobTitles: updatedJobTitles,
          updatedAt: new Date(),
          updatedBy: user?.id || 'system',
        });
        toast.success('تم تحديث الصلاحيات وحفظها في قاعدة البيانات بنجاح');
      } catch (dbError: any) {
        console.warn(
          'Failed to save to database, using local storage:',
          dbError
        );

        if (dbError?.code === 'permission-denied') {
          toast(
            'تم حفظ التغييرات محلياً - قيود الصلاحيات تمنع الحفظ في قاعدة البيانات',
            {
              icon: '⚠️',
              duration: 5000,
            }
          );
        } else {
          toast('تم حفظ التغييرات محلياً - فشل في الحفظ في قاعدة البيانات', {
            icon: '⚠️',
            duration: 5000,
          });
        }
      }

      setShowPermissionsModal(false);
      setSelectedJobTitle(null);
      setEditingPermissions(null);
    } catch (error: any) {
      console.error('Error saving permissions:', error);
      toast.error('فشل في حفظ الصلاحيات');
    } finally {
      setSaving(false);
    }
  };

  const getPermissionCount = (permissions: EmployeePermissions): number => {
    return Object.values(permissions).filter(Boolean).length;
  };

  // وظائف الإضافة والحذف والتعديل
  const handleAddJobTitle = async () => {
    if (!newJobTitleName.trim()) {
      toast.error('يرجى إدخال اسم المسمى الوظيفي');
      return;
    }

    const trimmedName = newJobTitleName.trim() as JobTitle;

    // تحقق من عدم وجود المسمى مسبقاً
    if (jobTitles.some(jt => jt.title === trimmedName)) {
      toast.error('هذا المسمى الوظيفي موجود بالفعل');
      return;
    }

    try {
      setSaving(true);

      // إنشاء صلاحيات افتراضية للمسمى الجديد
      const defaultPermissions = getEmployeePermissions(
        'IT' as Department,
        'Junior'
      );

      const newJobTitle = {
        title: trimmedName,
        permissions: defaultPermissions,
      };

      const updatedJobTitles = [...jobTitles, newJobTitle];
      setJobTitles(updatedJobTitles);

      // محاولة حفظ في Firebase
      try {
        await updateDoc(doc(db, 'system_settings', 'job_title_permissions'), {
          jobTitles: updatedJobTitles,
          updatedAt: new Date(),
          updatedBy: user?.id || 'system',
        });
        toast.success('تم إضافة المسمى الوظيفي بنجاح');
      } catch (dbError: any) {
        console.warn('Failed to save to database:', dbError);
        toast('تم إضافة المسمى محلياً - فشل في الحفظ في قاعدة البيانات', {
          icon: '⚠️',
          duration: 5000,
        });
      }

      setShowAddJobTitleModal(false);
      setNewJobTitleName('');
    } catch (error: any) {
      console.error('Error adding job title:', error);
      toast.error('فشل في إضافة المسمى الوظيفي');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteJobTitle = async () => {
    if (!jobTitleToDelete) return;

    try {
      setSaving(true);

      const updatedJobTitles = jobTitles.filter(
        jt => jt.title !== jobTitleToDelete
      );
      setJobTitles(updatedJobTitles);

      // محاولة حفظ في Firebase
      try {
        await updateDoc(doc(db, 'system_settings', 'job_title_permissions'), {
          jobTitles: updatedJobTitles,
          updatedAt: new Date(),
          updatedBy: user?.id || 'system',
        });
        toast.success('تم حذف المسمى الوظيفي بنجاح');
      } catch (dbError: any) {
        console.warn('Failed to save to database:', dbError);
        toast('تم حذف المسمى محلياً - فشل في الحفظ في قاعدة البيانات', {
          icon: '⚠️',
          duration: 5000,
        });
      }

      setShowDeleteConfirmModal(false);
      setJobTitleToDelete(null);
    } catch (error: any) {
      console.error('Error deleting job title:', error);
      toast.error('فشل في حذف المسمى الوظيفي');
    } finally {
      setSaving(false);
    }
  };

  const confirmDeleteJobTitle = (jobTitle: JobTitle) => {
    setJobTitleToDelete(jobTitle);
    setShowDeleteConfirmModal(true);
  };

  const getPermissionIcon = (key: string) => {
    const iconMap: { [key: string]: React.ReactNode } = {
      canViewStudents: <Users className='w-4 h-4' />,
      canCreateStudents: <Users className='w-4 h-4' />,
      canEditStudents: <Edit className='w-4 h-4' />,
      canDeleteStudents: <Trash2 className='w-4 h-4' />,
      canViewPayments: <DollarSign className='w-4 h-4' />,
      canCreatePayments: <DollarSign className='w-4 h-4' />,
      canEditPayments: <DollarSign className='w-4 h-4' />,
      canViewReports: <BarChart3 className='w-4 h-4' />,
      canCreateUsers: <Users className='w-4 h-4' />,
      canEditUsers: <Edit className='w-4 h-4' />,
      canExportData: <FileText className='w-4 h-4' />,
      canManageUsers: <Users className='w-4 h-4' />,
      canViewFinancials: <DollarSign className='w-4 h-4' />,
      canProcessPayments: <DollarSign className='w-4 h-4' />,
      canManageSchedule: <Calendar className='w-4 h-4' />,
      canAccessAssignments: <Calendar className='w-4 h-4' />,
    };
    return iconMap[key] || <Shield className='w-4 h-4' />;
  };

  const getPermissionLabel = (key: string): string => {
    const labelMap: { [key: string]: string } = {
      canViewStudents: 'عرض الطلاب',
      canCreateStudents: 'إضافة طلاب',
      canEditStudents: 'تعديل الطلاب',
      canDeleteStudents: 'حذف الطلاب',
      canViewPayments: 'عرض المدفوعات',
      canCreatePayments: 'إنشاء مدفوعات',
      canEditPayments: 'تعديل المدفوعات',
      canViewReports: 'عرض التقارير',
      canCreateUsers: 'إنشاء مستخدمين',
      canEditUsers: 'تعديل المستخدمين',
      canExportData: 'تصدير البيانات',
      canManageUsers: 'إدارة المستخدمين',
      canViewFinancials: 'عرض الماليات',
      canProcessPayments: 'معالجة المدفوعات',
      canManageSchedule: 'إدارة الجداول',
      canAccessAssignments: 'إدارة الواجبات',
    };
    return labelMap[key] || key;
  };

  if (loading) {
    return (
      <div className='flex items-center justify-center py-12'>
        <LoadingSpinner size='large' />
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='flex items-center justify-between'>
        <div>
          <h1 className='text-3xl font-bold text-secondary-800'>
            إدارة صلاحيات المسميات الوظيفية
          </h1>
          <p className='text-secondary-600 mt-2'>
            تخصيص وتعديل صلاحيات كل مسمى وظيفي في النظام
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className='border-b border-secondary-200'>
        <nav className='flex space-x-8 space-x-reverse'>
          <button
            onClick={() => setActiveTab('permissions')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'permissions'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-secondary-500 hover:text-secondary-700 hover:border-secondary-300'
            }`}
          >
            <Shield className='w-4 h-4 inline ml-2' />
            المسميات الوظيفية
          </button>
          <button
            onClick={() => setActiveTab('departments')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'departments'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-secondary-500 hover:text-secondary-700 hover:border-secondary-300'
            }`}
          >
            <Building className='w-4 h-4 inline ml-2' />
            إدارة الإدارات
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'permissions' ? (
        <div className='space-y-6'>
          {/* Search Bar and Add Button */}
          <div className='flex items-center gap-4'>
            <div className='relative flex-1 max-w-md'>
              <Search className='w-5 h-5 absolute right-3 top-3 text-secondary-400' />
              <input
                type='text'
                placeholder='البحث في المسميات الوظيفية...'
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className='input-field pr-10'
              />
            </div>
            <button
              onClick={() => setShowAddJobTitleModal(true)}
              className='btn-primary flex items-center gap-2'
            >
              <Plus className='w-4 h-4' />
              إضافة مسمى وظيفي
            </button>
          </div>

          {/* Job Titles Grid */}
          <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'>
            {filteredJobTitles.map(jobTitleData => (
              <div
                key={jobTitleData.title}
                className='bg-white rounded-lg border border-secondary-200 p-6 hover:shadow-md transition-shadow'
              >
                <div className='flex items-start justify-between mb-4'>
                  <div className='flex-1'>
                    <h3 className='text-lg font-semibold text-secondary-800'>
                      {getJobTitleDisplayName(jobTitleData.title)}
                    </h3>
                    <p className='text-secondary-600 text-sm mt-1'>
                      {getPermissionCount(jobTitleData.permissions)} صلاحية
                      مفعلة
                    </p>
                  </div>
                </div>

                {/* Permissions Preview */}
                <div className='mb-4'>
                  <div className='flex flex-wrap gap-1'>
                    {Object.entries(jobTitleData.permissions)
                      .filter(([, value]) => value)
                      .slice(0, 6)
                      .map(([key]) => (
                        <span
                          key={key}
                          className='inline-flex items-center gap-1 bg-primary-100 text-primary-800 text-xs px-2 py-1 rounded-full'
                        >
                          {getPermissionIcon(key)}
                          {getPermissionLabel(key)}
                        </span>
                      ))}
                    {getPermissionCount(jobTitleData.permissions) > 6 && (
                      <span className='text-xs text-secondary-500'>
                        +{getPermissionCount(jobTitleData.permissions) - 6} أخرى
                      </span>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className='flex justify-between items-center'>
                  <div className='flex gap-2'>
                    <button
                      onClick={() => handleEditPermissions(jobTitleData.title)}
                      className='bg-blue-100 hover:bg-blue-200 text-blue-700 p-2 rounded-lg transition-colors'
                      title='تعديل الصلاحيات'
                    >
                      <Settings className='w-4 h-4' />
                    </button>
                    <button
                      onClick={() => confirmDeleteJobTitle(jobTitleData.title)}
                      className='bg-red-100 hover:bg-red-200 text-red-700 p-2 rounded-lg transition-colors'
                      title='حذف المسمى الوظيفي'
                    >
                      <Trash2 className='w-4 h-4' />
                    </button>
                  </div>
                  <button
                    onClick={() => handleEditPermissions(jobTitleData.title)}
                    className='btn-primary flex items-center text-sm'
                  >
                    <Eye className='w-4 h-4 ml-1' />
                    عرض التفاصيل
                  </button>
                </div>
              </div>
            ))}
          </div>

          {filteredJobTitles.length === 0 && (
            <div className='text-center py-12'>
              <Shield className='w-16 h-16 text-secondary-300 mx-auto mb-4' />
              <h3 className='text-lg font-medium text-secondary-600 mb-2'>
                لا توجد مسميات وظيفية
              </h3>
              <p className='text-secondary-500'>
                لا توجد مسميات وظيفية تطابق معايير البحث
              </p>
            </div>
          )}

          {/* Edit Permissions Modal */}
          {showPermissionsModal && selectedJobTitle && editingPermissions && (
            <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50'>
              <div className='bg-white rounded-lg max-w-4xl w-full max-h-screen overflow-y-auto'>
                <div className='p-6 border-b border-secondary-200'>
                  <div className='flex items-center justify-between'>
                    <div>
                      <h2 className='text-xl font-bold text-secondary-800'>
                        تعديل صلاحيات:{' '}
                        {getJobTitleDisplayName(selectedJobTitle)}
                      </h2>
                      <p className='text-secondary-600 mt-1'>
                        اختر الصلاحيات المناسبة لهذا المسمى الوظيفي
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        setShowPermissionsModal(false);
                        setSelectedJobTitle(null);
                        setEditingPermissions(null);
                      }}
                      className='text-secondary-400 hover:text-secondary-600'
                      aria-label='إغلاق'
                    >
                      <X className='w-6 h-6' />
                    </button>
                  </div>
                </div>

                <div className='p-6'>
                  <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                    {Object.entries(editingPermissions).map(([key, value]) => (
                      <div
                        key={key}
                        className='flex items-center justify-between p-3 border border-secondary-200 rounded-lg'
                      >
                        <div className='flex items-center gap-3'>
                          {getPermissionIcon(key)}
                          <span className='text-secondary-800'>
                            {getPermissionLabel(key)}
                          </span>
                        </div>
                        <label className='relative inline-flex items-center cursor-pointer'>
                          <input
                            type='checkbox'
                            checked={value}
                            disabled={saving}
                            aria-label={`تفعيل ${getPermissionLabel(key)}`}
                            onChange={e => {
                              setEditingPermissions(prev => ({
                                ...prev!,
                                [key]: e.target.checked,
                              }));
                            }}
                            className='sr-only peer'
                          />
                          <div className="relative w-11 h-6 bg-secondary-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-secondary-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                        </label>
                      </div>
                    ))}
                  </div>

                  <div className='flex justify-end mt-6 space-x-3 space-x-reverse'>
                    <button
                      onClick={handleSavePermissions}
                      disabled={saving}
                      className='btn-primary flex items-center disabled:opacity-50 disabled:cursor-not-allowed'
                    >
                      {saving ? (
                        <>
                          <div className='animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent ml-2'></div>
                          جار الحفظ...
                        </>
                      ) : (
                        <>
                          <Save className='w-4 h-4 ml-2' />
                          حفظ التغييرات
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => {
                        setShowPermissionsModal(false);
                        setSelectedJobTitle(null);
                        setEditingPermissions(null);
                      }}
                      disabled={saving}
                      className='btn-secondary'
                    >
                      إلغاء
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Add Job Title Modal */}
          {showAddJobTitleModal && (
            <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
              <div className='bg-white rounded-lg p-6 w-full max-w-md mx-4'>
                <div className='flex items-center justify-between mb-4'>
                  <h3 className='text-lg font-semibold text-secondary-800'>
                    إضافة مسمى وظيفي جديد
                  </h3>
                  <button
                    onClick={() => {
                      setShowAddJobTitleModal(false);
                      setNewJobTitleName('');
                    }}
                    className='text-secondary-400 hover:text-secondary-600'
                    aria-label='إغلاق المودال'
                  >
                    <X className='w-5 h-5' />
                  </button>
                </div>

                <div className='space-y-4'>
                  <div>
                    <label className='block text-sm font-medium text-secondary-700 mb-2'>
                      اسم المسمى الوظيفي
                    </label>
                    <input
                      type='text'
                      value={newJobTitleName}
                      disabled={saving}
                      onChange={e => setNewJobTitleName(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter' && newJobTitleName.trim() && !saving) {
                          handleAddJobTitle();
                        }
                      }}
                      placeholder='مثال: محلل أنظمة'
                      className='input-field w-full disabled:cursor-not-allowed disabled:bg-secondary-100 disabled:text-secondary-500'
                      autoFocus
                    />
                  </div>

                  <div className='flex justify-end space-x-3 space-x-reverse'>
                    <button
                      onClick={handleAddJobTitle}
                      disabled={saving || !newJobTitleName.trim()}
                      className='btn-primary flex items-center disabled:opacity-50'
                    >
                      {saving ? (
                        <>
                          <div className='animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent ml-2'></div>
                          جار الإضافة...
                        </>
                      ) : (
                        <>
                          <UserPlus className='w-4 h-4 ml-2' />
                          إضافة
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => {
                        setShowAddJobTitleModal(false);
                        setNewJobTitleName('');
                      }}
                      disabled={saving}
                      className='btn-secondary'
                    >
                      إلغاء
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Delete Confirmation Modal */}
          {showDeleteConfirmModal && jobTitleToDelete && (
            <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
              <div className='bg-white rounded-lg p-6 w-full max-w-md mx-4'>
                <div className='flex items-center justify-between mb-4'>
                  <h3 className='text-lg font-semibold text-red-600'>
                    تأكيد الحذف
                  </h3>
                  <button
                    onClick={() => {
                      setShowDeleteConfirmModal(false);
                      setJobTitleToDelete(null);
                    }}
                    className='text-secondary-400 hover:text-secondary-600'
                    aria-label='إغلاق المودال'
                  >
                    <X className='w-5 h-5' />
                  </button>
                </div>

                <div className='space-y-4'>
                  <div className='flex items-center gap-3'>
                    <AlertCircle className='w-8 h-8 text-red-500' />
                    <div>
                      <p className='text-secondary-800 font-medium'>
                        هل أنت متأكد من حذف المسمى الوظيفي؟
                      </p>
                      <p className='text-secondary-600 text-sm mt-1'>
                        "{getJobTitleDisplayName(jobTitleToDelete)}"
                      </p>
                      <p className='text-red-600 text-sm mt-2'>
                        لا يمكن التراجع عن هذا الإجراء
                      </p>
                    </div>
                  </div>

                  <div className='flex justify-end space-x-3 space-x-reverse'>
                    <button
                      onClick={handleDeleteJobTitle}
                      disabled={saving}
                      className='bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg flex items-center disabled:opacity-50'
                    >
                      {saving ? (
                        <>
                          <div className='animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent ml-2'></div>
                          جار الحذف...
                        </>
                      ) : (
                        <>
                          <Trash2 className='w-4 h-4 ml-2' />
                          حذف
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => {
                        setShowDeleteConfirmModal(false);
                        setJobTitleToDelete(null);
                      }}
                      disabled={saving}
                      className='btn-secondary'
                    >
                      إلغاء
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        <DepartmentManagement />
      )}
    </div>
  );
};

export default RolesManagement;
